<template>
  <div
    ref="presentPublication"
    :class="{
      'nav-bar-opened': showSearchNavigation,
      'pointer-hidden': isPointerHidden,
      'tab-changing': isTabChanging
    }"
    :style="wrappingReadSettingsStyles"
    class="present-publication"
  >
    <SearchNavigation
      v-if="showSearchNavigation"
      :publication-id="publicationId"
      @searchNavigationEvent="searchNavigationEventHandler"
    />

    <transition
      :name="SCROLL_BLOCK_CLASS"
      @before-enter="debSetTransition(false)"
      @after-enter="debSetTransition(true)"
    >
      <BookScroll
        :is-book-tab-active="isBookTabActive"
        @BookScrollEvent="bookScrollEventHandler"
      >
        <template #meta-block>
          <MetaBlock
            :book-id="publicationId"
            @metablockEvent="metablockEventHandler"
          />
        </template>
        <template #side-bar>
          <AnnotationSidebar
            :book-id="materialsId"
            :click-event-handler="itemClickEvent"
            :container-selector="scrollAreaSelector"
            :show-sidebar="isAnnotationSidebarShown"
            @annotationSidebarEvent="annotationSidebarEventHandler"
          />
        </template>
        <template #context-menu>
          <ContextMenu
            v-if="showContextMenu && componentDisplayRules.contextMenu"
            :container-selector="scrollAreaSelector"
            :menu-open-context="menuOpenContext"
            @menuEvent="contextMenuHandler"
          />
        </template>
        <template #text-selection>
          <TextSelection
            v-if="showSelection"
            :element-selector="scrollAreaSelector"
            :ignored-classes-for-selection="ignoredClassesForSelection"
            :scroll-selector="PUBLICATION_SCROLL_CLASS"
            :task="selectionTask"
            @SelectionEvent="selectionEventHandler"
          />
        </template>
      </BookScroll>
    </transition>

    <CompilationEditContextMenu
      v-if="shownCompilationEditMenu"
      :context="compilationEditMenuContext"
      @compilationMenuEvent="compilationMenuEventHandler"
    />
    <div
      :class="{
        'is-compilation-open': isCompilationOpen
      }"
      class="publication-bottom-controls"
    >
      <ProgressToolbar
        v-if="isProgressToolbarDisplay"
        v-show="isProgressToolbarShown"
        :book-id="publicationId"
        :materials-id="materialsId"
        :publication-type="publicationType"
        @progressToolbarEvent="progressToolbarEventHandler"
      />
      <FloatingPlayButton
        v-if="isShownFloatingPlayButton"
        :class="BUTTON_CLASS"
        :disabled="isFloatingPlayButtonDisabled"
        class="floating-button-wrapper"
        @floatingPlayButtonEvent="floatingPlayButtonEventHandler"
      />
    </div>
    <PositionTracker
      v-if="isPositionTrackerAvailable"
      :book-id="publicationId"
      :publication-type="publicationType"
      @positionTrackerEvent="positionTrackerEventHandler"
    />
    <ReadingArea
      v-if="componentDisplayRules.readingArea && isBookTabActive"
      @readingAreaEvent="readingAreaEventHandler"
    />
    <AudioTracker
      v-if="isAudioTrackerAvailable"
      :publication-type="publicationType"
      @audioTrackerEvent="audioTrackerEventHandler"
    />
    <ParaNoteContextMenu
      v-if="
        showParaNoteContextMenu && componentDisplayRules.paraNoteContextMenu
      "
      :para-note-menu-params="paraNoteMenuParams"
      @paraNoteContextMenuEvent="paraNoteMenuEventHandler"
    />
    <AnnotationAudio
      v-if="componentDisplayRules.annotationAudio"
      :book-id="publicationId"
      @annotationAudioEvent="annotationAudioHandler"
    />
    <EssayTaskPopup
      v-if="showEssayTaskPopup && componentDisplayRules.essayTaskPopup"
      :essay-task-popup-params="essayTaskPopupParams"
      @essayTaskPopupEvent="essayTaskPopupEventHandler"
    />
    <div v-if="showTestEditor && componentDisplayRules.testEditor">
      <TestEditor
        :test-editor-params="testEditorParams"
        @testEditorEvent="testEditorEventHandler"
      />
    </div>
    <AudioPlayer
      v-if="isAudioAvailable"
      @audioPlayerEvent="audioPlayerEventHandler"
    />
    <CompilationEditToolbar
      v-if="isCompilationEditMode && componentDisplayRules.compEditToolbar"
      :compilation-id="publicationId"
      @compilationEditToolbarEvent="compilationEditToolbarEventHandler"
    />
  </div>
</template>

<script>
import LoggerFactory from '@/services/utils/LoggerFactory';
import debounce from 'lodash/debounce';
import get from 'lodash/get';

import BookScrollEnum from '@/enums/BookScrollEnum';
import CompilationEventsEnum from '@/enums/CompilationEventsEnum';
import MetablockEventEnum from '@/enums/MetablockEventEnum';
import ContextMenuFactory from '@/classes/factories/ContextMenuFactory';
import ContextMenuParamsFactory from '@/classes/factories/Materials/ContextMenuParamsFactory';
import BookScroll from '@/components/views/BookScroll/BookScroll';

import AnnotationSidebar from '@/components/views/AnnotationSidebar/AnnotationSidebar';
import ContextMenu from '@/components/views/ContextMenu/ContextMenu';

import TextSelection from '@/components/views/TextSelection/TextSelection';
import ProgressToolbar from '@/components/views/ProgressToolbar/ProgressToolbar.vue';
import PositionTracker from '@/components/views/PositionTracker/PositionTracker.vue';
import ReadingArea from '@/components/views/ReadingArea/ReadingArea.vue';
import AnnotationAudio from '@/components/views/Audio/AnnotationAudio';
import ParaNoteContextMenu from '@/components/views/ParaNoteContextMenu/ParaNoteContextMenu';
import EssayTaskPopup from '@/components/views/EssayTaskPopup/EssayTaskPopup';
import FloatingPlayButton from '@/components/views/FloatingPlayButton/FloatingPlayButton.vue';
import CompilationEditContextMenu from '@/components/views/Compilations/CompilationEditContextMenu';
import AudioPlayer from '@/components/views/Audio/AudioPlayer';
import SearchNavigation from '@/components/views/SearchNavigation/SearchNavigation';
import AudioTracker from '@/components/views/PositionTracker/AudioTracker.vue';
import CompilationEditToolbar from '@/components/views/Compilations/CompilationEditToolbar';
import MetaBlock from '@/components/views/MetaBlock/MetaBlock';

import FeatureDetectorService from '@/services/FeatureDetector/FeatureDetectorService';

import TextSelectionTaskEnum from '@/enums/TextSelectionTaskEnum';
import MaterialTypes from '@shared/enums/MaterialTypes';
import MenuTasks from '@/enums/MenuTasks';
import MenuEventEnum from '@/enums/MenuEventEnum';
import PopupEventEnum from '@/enums/PopupEventEnum';
import AnnotationAudioEvents from '@/enums/AnnotationAudioEvents';
import CompilationEditToolbarEvents from '@/enums/CompilationEditToolbarEvents';
import AnnotationInfoPopupEvents from '@/enums/AnnotationInfoPopupEvents';
import PublicationsTypesEnum from '@shared/enums/PublicationsTypesEnum';
import SelectionEvents from '@/enums/SelectionEvents';
import CompilationSelectionTypes from '@/enums/CompilationSelectionTypes';
import ProgressToolbarEvents from '@/enums/ProgressToolbarEvents';
import AnnotationSidebarEvents from '@/enums/AnnotationSidebarEvents';
import ParaNoteContextMenuEvents from '@/enums/ParaNoteContextMenuEvents';
import PositionTrackerEvents from '@/enums/PositionTrackerEvents';
import AudioPlayerEvents from '@/enums/AudioPlayerEvents';
import ReadingAreaEvents from '@/enums/ReadingAreaEvents';
import TestEditorEvents from '@/enums/TestEditorEvents';
import EssayTaskPopupEvents from '@/enums/EssayTaskPopupEvents';
import AudioTrackerEvents from '@/enums/AudioTrackerEvents';
import CustomErrorEnum from '@/enums/CustomErrorEnum';

import SearchNavigationEventEnum from '@/enums/SearchNavigationEventEnum';
import PresentPublicationEventsEnum from '@/enums/PresentPublicationEventsEnum';
import PopupNamesEnum from '@/enums/PopupNamesEnum';
import FloatingPlayButtonEventEnum from '@/enums/FloatingPlayButtonEventEnum';

import PromiseUtil from '@/services/utils/PromiseUtil';
import PublicationNavigateLogicService from '@/services/PublicationLogic/PublicationNavigateLogicService';
import PublicationLogicService from '@/services/PublicationLogic/PublicationLogicService';
import PublicationScrollLogicService from '@/services/PublicationLogic/PublicationScrollLogicService';

import TextUtils from '@shared/publication/dom-utils/text-utils.mjs';
import MarkerUtils from '@shared/publication/dom-utils/marker-utils.mjs';
import GoogleAnalyticsUtils from '@/services/utils/GoogleAnalyticsUtils';

import Locator from '@shared/publication/locator.mjs';
import AppConstantsUtil from '@/services/utils/AppConstantsUtil';
import SelectionConstantUtils from '@shared/publication/selection/SelectionConstantUtils.mjs';
import images from '@/assets/images';

const logger = LoggerFactory.getLogger('PresentPublication.vue');

const DEBOUNCE_TIME = 200;
const IGNORED_CLASSES_FOR_SELECTION = [
  AppConstantsUtil.ANNOTATION_SIDEBAR,
  AppConstantsUtil.ANNOTATION_INFO_POPUP,
  SelectionConstantUtils.CONTEXT_MENU,
  AppConstantsUtil.FOOTNOTE_CLASS,
  AppConstantsUtil.META_BLOCK_TABS_CLASS
];

export default {
  name: 'PresentPublication',
  components: {
    BookScroll,

    AnnotationSidebar,
    ContextMenu,

    MetaBlock,
    TextSelection,
    ProgressToolbar,
    SearchNavigation,
    PositionTracker,
    ReadingArea,
    AudioTracker,
    FloatingPlayButton,
    CompilationEditContextMenu,

    ParaNoteContextMenu,
    AnnotationAudio,
    EssayTaskPopup,
    TestEditor: () => import('@/components/views/TestEditor/TestEditor.vue'),
    AudioPlayer,
    CompilationEditToolbar
  },

  data() {
    const currentMode = this.$store.getters['ContextStore/appModeGetter'];

    const BUTTON_CLASS = AppConstantsUtil.PLAY_BUTTON_CLASS;
    const SCROLL_BLOCK_CLASS = AppConstantsUtil.SCROLL_BLOCK_CLASS;
    const shortTapHandler = PublicationLogicService.shortTapHandler.bind(
      this,
      this.$store
    );
    const debSetTransition = debounce(this.setTransition, 300);
    return {
      debSetTransition,
      shortTapHandler,
      recalculateLayout: false,
      eventsOnce: {},
      isPointerHidden: false,
      isCompilationOpen: true,

      BUTTON_CLASS,
      SCROLL_BLOCK_CLASS,

      animateScroll: null,
      currentMode,

      PUBLICATION_SCROLL_CLASS: AppConstantsUtil.PUBLICATION_SCROLL_CLASS,

      errorMessage: '',
      initedOnStable: false,

      isOutOfBackground: false,
      shouldPresentErrorToaster: false,
      showContextMenu: false,
      showParaNoteContextMenu: false,
      showEssayTaskPopup: false,
      showTestEditor: false,
      ignoredClassesForSelection: IGNORED_CLASSES_FOR_SELECTION,
      selectionTask: null,
      shownCompilationEditMenu: false,
      compilationEditMenuContext: {
        menuPosition: null,
        paraId: null
      },

      showAnnotationSidebar: false,
      hideProgressToolbar: true,
      annContainerSelector: '',
      itemClickEvent: null,
      scrollTimeout: null,
      subscribeAction: null,
      // to hide components if error occurred
      componentDisplayRules: {
        progressToolbar: true,
        selection: true,
        metaBlock: true,
        annSidebar: true,
        positionTracker: true,
        readingArea: true,
        contextMenu: true,
        compEditToolbar: true,
        searchNavigation: true,
        paraNoteContextMenu: true,
        testEditor: true,
        essayTaskPopup: true,
        annotationAudio: true,
        audioPlayer: true
      },
      isOutOfBgTimeout: 0,
      updateRadingAreaDefer: null,
      hidePointerTimeout: 0,
      mediaChange: false,
      SUBSCRIBE_MEDIA_TRIGGERED_TIME: 200,

      // Tab navigation state
      activeMetaTab: 'book',
      isBookTabActive: true,
      isTabChanging: false
    };
  },

  computed: {
    transitionOver() {
      return this.$store.getters['PublicationStore/isTransitionOver'];
    },
    isSamePublication() {
      return this.$store.getters['OpenParameterStore/isSamePublication'](
        this.$route
      );
    },
    contentHighlight() {
      return this.$store.getters['OpenParameterStore/getTextHighlight'];
    },
    meta() {
      return this.$store.getters['PublicationStore/getMeta'](
        this.publicationId
      );
    },
    scrollAreaSelector() {
      return this.$store.getters['PublicationStore/getScrollAreaSelector'];
    },
    publicationType() {
      return this.$store.getters['OpenParameterStore/getPublicationType'];
    },
    editMode() {
      return this.$store.getters['OpenParameterStore/getEditMode'];
    },
    publicationId() {
      return this.$store.getters['OpenParameterStore/getPublicationId'];
    },
    openParaId() {
      return this.$store.getters['OpenParameterStore/getParaId'];
    },
    appState() {
      return this.$store.getters['ContextStore/appState'];
    },
    isAudioBook() {
      return this.$store.getters['PublicationStore/isAudioPublication'](
        this.publicationId
      );
    },
    isShownTextSelection() {
      return this.$store.getters['PublicationStore/getIsShownTextSelection'];
    },
    isFABDisabled() {
      return this.$store.getters['PublicationStore/getIsFABDisabled'];
    },
    isWide() {
      return this.$store.getters['MediaDetectorStore/mediaSize'].wide;
    },
    paraNoteMenuParams() {
      return ContextMenuParamsFactory.createParaNoteMenuParams({
        reqParams: this.reqParams
      });
    },
    essayTaskPopupParams() {
      return ContextMenuParamsFactory.createEssayTaskPopupParams({
        reqParams: this.reqParams
      });
    },
    reqParams() {
      return this.$store.getters['MaterialsStore/getReqParams'];
    },
    testEditorParams() {
      return ContextMenuParamsFactory.createTestEditorParams({
        materialsId: this.materialsId
      });
    },
    selectedLocator() {
      return this.$store.getters['MaterialsStore/getSelectedLocator'];
    },
    materialsId() {
      return this.publicationId;
    },
    needRepositionOnScroll() {
      return this.$store.getters['PublicationStore/isNeedReposition'];
    },
    isDragging() {
      return this.$store.getters['PlaybackStore/isDragging']();
    },

    readingArea() {
      return this.$store.getters['ProgressStore/getReadingArea'];
    },
    categoriesStyles() {
      return this.$store.getters['AnnotationsStore/getCategoriesStyles'];
    },
    getPlayingAnnId() {
      return this.$store.getters['AnnotationsStore/getPlayingAnnId']();
    },

    readingSettingsView() {
      return this.$store.getters['ReadingSettingsStore/getReadingSettingsView'];
    },
    isCreateAnnotationPopupOpen() {
      return this.$store.getters['ManagePopupStore/isPopupOpened'](
        PopupNamesEnum.CREATE_ANNOTATION_POPUP
      );
    },
    isExtrasOpened() {
      return this.$store.getters['ManagePopupStore/isPopupOpened'](
        PopupNamesEnum.EXTRAS_POPUP
      );
    },
    isAnnInfoPopupOpened() {
      return this.$store.getters['ManagePopupStore/isPopupOpened'](
        PopupNamesEnum.ANNOTATION_INFO_POPUP
      );
    },
    isDictionaryAutoOpen() {
      const isAllOptionsDisabled =
        !this.readingSettingsView.googleTranslate &&
        !this.readingSettingsView.dictionaryMode &&
        !this.readingSettingsView.wikiMode;

      return (
        this.readingSettingsView.dictionaryAutoOpen && !isAllOptionsDisabled
      );
    },
    isGuest() {
      return this.$store.getters['UserStore/isGuestUser'];
    },
    wrappingReadSettingsStyles() {
      return this.$store.getters[
        'ReadingSettingsStore/getWrappingReadingSettingsStyles'
      ];
    },
    currentScrubberLocator() {
      return this.$store.getters['PlaybackStore/getCurrentLocator'];
    },
    isAudioAvailable() {
      return this.isAudioBook && this.componentDisplayRules.audioPlayer;
    },
    isPositionTrackerAvailable() {
      return (
        this.readingArea &&
        this.componentDisplayRules.positionTracker &&
        !this.isCompilationOpen &&
        !this.isViewMode
      );
    },
    isAudioTrackerAvailable() {
      return this.isAudioAvailable && !this.isCompilationOpen;
    },
    readSettingsStyles() {
      return this.$store.getters[
        'ReadingSettingsStore/getReadingSettingsStyles'
      ];
    },
    menuOpenContext() {
      const parameters = this.$store.getters['ContextStore/parameters'];
      const clientNodeContext = this.$store.getters[
        'ContextStore/clientNodeContextGetter'
      ];
      const meta =
        this.$store.getters['PublicationStore/getMeta'](this.publicationId) ||
        {};
      const isOffline = !this.isOnline;
      const hasAudio = meta.audio;
      const isAdmin = this.$store.getters['UserStore/isUserAdmin'];
      const isUserEditor = this.$store.getters['UserStore/isUserEditor'];
      const isGuest = this.isGuest;
      const isDesktop = clientNodeContext.platformType === 'Desktop';
      const isDevice = clientNodeContext.isDevice;
      var hasIlmId = !!meta.ilmId;
      const isVisibleCopyInSafari =
        window.safari ||
        (window.navigator.platform === 'iPad' && !window.cordova);
      const isCompilation =
        this.publicationType === PublicationsTypesEnum.COMPILATION;

      const builder = ContextMenuFactory.getMenuOpenContextBuilder();
      return builder
        .setIsCompilation(isCompilation)
        .setIsEditor(this.$store.getters['ContextStore/isEditorApp'])
        .setIsReader(this.$store.getters['ContextStore/isReaderApp'])
        .setIsOffline(isOffline)
        .setHasAudio(hasAudio)
        .setIsAdmin(isAdmin)
        .setIsUserEditor(isUserEditor)
        .setIsGuest(isGuest)
        .setIsDesktop(isDesktop)
        .setIsDevice(isDevice)
        .setHasIlmId(hasIlmId)
        .setBrand(this.$store.getters['ContextStore/brand'])
        .setIsVisibleCopyInSafari(isVisibleCopyInSafari)
        .setShowReportAboutErrors(parameters.showReportAboutErrors)
        .setReaderIsEqualToStudent(parameters.readerIsEqualToStudent)
        .build();
    },
    isAnnotationSidebarShown() {
      return (
        this.showAnnotationSidebar &&
        this.componentDisplayRules.annSidebar &&
        !this.isCompilationOpen &&
        this.transitionOver
      );
    },
    isProgressToolbarDisplay() {
      const hasMeta = Boolean(
        this.$store.getters['PublicationStore/getMeta'](this.publicationId)
      );
      const hasContent =
        (this.$store.getters['PublicationStore/getParagraphItems'] || [])
          .length !== 0;
      return hasMeta && hasContent && !this.isViewMode;
    },
    showSelection() {
      return (
        this.isShownTextSelection &&
        this.componentDisplayRules.selection &&
        !this.isViewMode
      );
    },
    isProgressToolbarShown() {
      return (
        !this.hideProgressToolbar &&
        this.componentDisplayRules.progressToolbar &&
        !this.isCompilationEditMode
      );
    },
    isShownFloatingPlayButton() {
      return (
        this.isAudioBook &&
        !this.isMetaBlockInViewport &&
        !this.hideProgressToolbar &&
        this.componentDisplayRules.progressToolbar
      );
    },
    isAudioPlaying() {
      return this.$store.getters['PlaybackStore/isAudioPlaying'];
    },
    isAppInBackground() {
      return this.$store.getters['ContextStore/isAppInBackground'];
    },
    isWebBackground() {
      return this.$store.getters['ContextStore/isWebBackground'];
    },
    isAndroid() {
      return this.$store.getters['ContextStore/isAndroid'];
    },
    isIos() {
      return this.$store.getters['ContextStore/isIos'];
    },
    isOnline() {
      return this.$store.getters['ContextStore/isOnline'];
    },
    navigationList() {
      return this.$store.getters['BookStore/getSearchNavigation']();
    },
    isNavigationFilled() {
      return this.navigationList?.navigateList?.length || false;
    },
    isBookSearchShown() {
      return this.$store.getters['ContextStore/getIsBookSearchShown'];
    },
    isFromSearchShown() {
      return this.$store.getters['ContextStore/getIsFromSearchShown'];
    },
    showSearchNavigation() {
      return (
        (this.isFromSearchShown || this.isBookSearchShown) &&
        this.isOnline &&
        !this.isCompilationOpen
      );
    },
    isMetaBlockInViewport() {
      return this.$store.getters['ContextStore/getIsMetaBlockInViewport'];
    },
    readingAreaStartLocator() {
      const startPara = get(this.readingArea, 'startLocator', null);
      return startPara;
    },
    isCompilationEditMode() {
      return this.$store.state.CompilationsStore.isEditMode;
    },
    limitedAccess() {
      return this.$store.getters['PublicationStore/isLimitedAccess'](
        this.publicationId
      );
    },
    clientReaderUrl() {
      return this.$store.getters['ContextStore/getClientReaderUrl'];
    },
    isDictionaryPopupOpened() {
      return this.$store.getters['ManagePopupStore/isPopupOpened'](
        PopupNamesEnum.DICTIONARY_POPUP
      );
    },
    isFloatingPlayButtonDisabled() {
      return this.isFABDisabled || this.isCompilationEditMode;
    },
    isViewMode() {
      return this.$store.getters['ContextStore/isViewMode'];
    },
    contentLength() {
      return this.$store.getters['PublicationStore/contentLength'];
    },
    isNightTheme() {
      return this.$store.getters['ReadingSettingsStore/isNightTheme'];
    },
    isDisplayScrubber() {
      return this.$store.getters['PlaybackStore/isDisplayScrubber'];
    },
    isScrolling() {
      return this.$store.getters['ContextStore/getIsScrolling'];
    }
  },
  watch: {
    async readingArea(newVal, oldVal) {
      const readingAreaReady = newVal && !oldVal;
      if (readingAreaReady && !this.isAudioPlaying) {
        this.$_restoreScrubberPosition();
      }
    },
    async meta(newMeta) {
      await this.$_initReadingSettingsStore(newMeta);
    },
    async contentLength() {
      if (this.meta) {
        await this.$_initReadingSettingsStore(this.meta);
      }
    },
    openParaId(newParaId) {
      if (!newParaId || !this.isSamePublication) {
        return;
      }
      this.showContextMenu = false;
    },
    isDragging(val) {
      if (val) {
        this.$store.commit(
          'PublicationStore/setSkipCurrentLocatorChange',
          true
        );
      }
    },
    isCompilationEditMode(isEditMode) {
      if (isEditMode) {
        this.$store.dispatch('PlaybackStore/pause');
      } else {
        this.$_moveScrubberToCorrectLocator();
      }
    },
    showSearchNavigation(show) {
      if (!show) {
        this.searchNavigationEventHandler({
          type: SearchNavigationEventEnum.CLOSE_SEARCH_NAV_BAR,
          data: {}
        });
      }
    },
    isAudioPlaying(isPlaying) {
      const isAudioStartPlaying = this.$store.getters[
        'PublicationStore/getIsAudioStartPlaying'
      ];
      this.$store.commit(
        'PublicationStore/setIsAudioStartPlaying',
        !isAudioStartPlaying && isPlaying
      );
      if (isPlaying) {
        this.$_removeBookSelectionAndOverlays();
        this.$_addShortTapListener();
        this.$_resetHighlight();
        this.$_hideSearchInsideBookIfNeeded();
      } else {
        this.$_removeShortTapListener();
      }
    },
    async isAppInBackground(isInBackground) {
      if (isInBackground) {
        this.$store.dispatch('PlaybackStore/hideScrubber');
      } else {
        if (this.isIos) {
          this.$_setScrubberPosition();
        }

        if (this.currentScrubberLocator) {
          const scrubberLocator = Locator.deserialize(
            this.currentScrubberLocator
          );
          PublicationScrollLogicService.scrollByLocator(
            this.$store,
            this.$router,
            scrubberLocator
          );
          this.$store.dispatch('PlaybackStore/showScrubberByLocator', {
            bookId: this.publicationId,
            InTextLocator: scrubberLocator.startLocator
          });
        }
        this.$_setIsOutOfBackground();

        if (this.isIos && this.isAudioPlaying) {
          this.$store.dispatch('PlaybackStore/continue');
        }
      }
    },
    isWebBackground() {
      this.onVisibilityChange();
    },
    readingAreaStartLocator() {
      if (this.readingArea) {
        this.stopPlayIfOutOfReadingArea();
      }
    },
    async currentScrubberLocator(newScrubberLocator, oldScrubberLocator) {
      await PublicationScrollLogicService.applyScrollOnScrubberChange(
        this.$store,
        this.$router,
        newScrubberLocator,
        oldScrubberLocator,
        this.isOutOfBackground
      );
    },
    publicationId() {
      this.$store.dispatch('ManagePopupStore/closeReturnToReadingToaster');
    },
    appState() {
      this.$store.dispatch('ManagePopupStore/closeReturnToReadingToaster');
    },
    isNightTheme(val) {
      this.$vuetify.theme.dark = val;
    }
  },
  created() {
    this.isCompilationOpen = this.$store.getters[
      'PublicationStore/isCompilationOpen'
    ];
  },
  async mounted() {
    if (process.client) {
      window.toggleWebBackgroundMode = async () => {
        const visible = this.$store.getters['ContextStore/isWebBackground'];
        this.$store.commit('ContextStore/setWebBackgroundMode', !visible);
      };
    }
    logger.info('Start handle mounted');
    //TODO: [nuxt] check reading settings font uploaded and proceed
    await this.$_initReadingSettingsStore(this.meta);
    this.$_subscribe();
    this.$_trackPresentPublicationOpening();
    logger.info('Handled mounted');

    this.$vuetify.theme.dark = this.isNightTheme;
  },
  async beforeDestroy() {
    this.$_resetHidingPointer();
    this.$store.commit('ContextStore/setIsBookSearchShown', false);
    this.$store.commit('ContextStore/setIsFromSearchShown', false);

    if (this.$refs.presentPublication) {
      this.$refs.presentPublication.removeEventListener(
        'mousemove',
        this.$_hidePointerWhenInactive
      );
      this.$refs.presentPublication.removeEventListener(
        'mousedown',
        this.$_clearNativeSelection
      );
    }
    if (!this.isCompilationOpen) {
      await this.$_updateUserActivity();
    }
    this.$vuetify.theme.dark = false;
    this.$store.dispatch('ContextStore/setVuetifyDarkMode', false);
  },
  destroyed() {
    this.$_unsubscribe();
    this.$store.dispatch('ManagePopupStore/closeAll');

    PublicationScrollLogicService.stopAnimateScroll(this.$store);
    if (this.$store.getters['ContextStore/isDevice']) {
      window.plugins.insomnia.allowSleepAgain();
    }
    this.$store.dispatch('ProgressStore/stopLoadReadingPosition');
    this.$_clearReadingSettings();
    this.$_clearMaterials();
    this.$_updateMetaBlockInViewport(false);
    this.$_updateStableContent(false);
    PublicationScrollLogicService.updateIsScrolling(this.$store, false);
    this.$_reinitPlaybackStore();
  },
  methods: {
    setTransition(transitionOver) {
      this.$store.commit('PublicationStore/setTransitionOver', transitionOver);
    },
    metablockEventHandler(payload) {
      const { type } = payload;
      switch (type) {
        case MetablockEventEnum.ERROR: {
          const error = payload.data.error;
          if (error.name === CustomErrorEnum.EMPTY_DATA) {
            this.componentDisplayRules.metaBlock = false;
          }
          this.bookScrollEventHandler({
            type: BookScrollEnum.METABLOCK_ERROR,
            data: payload.data
          });
          break;
        }
        case MetablockEventEnum.UPDATED_BOOK: {
          this.bookScrollEventHandler({
            type: BookScrollEnum.UPDATED_BOOK,
            data: payload.data
          });
          break;
        }
        case 'tabChanged': {
          const { activeTab = 'book', isBookTab = true } = payload?.data || {};

          this.isTabChanging = true;

          if (this.isBookTabActive && !isBookTab) {
            this.$_preserveScrollPosition();
          }

          this.activeMetaTab = activeTab;
          this.isBookTabActive = isBookTab;

          this.$nextTick(() => {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                this.isTabChanging = false;
              });
            });
          });

          break;
        }
        default: {
          break;
        }
      }
    },
    async $_initReadingSettingsStore(bookMeta) {
      if (!bookMeta) {
        return;
      }
      const audioSpeed =
        bookMeta.wordsPerMinute || AppConstantsUtil.DEFAULT_AUDIO_SPEED;
      const highlightMode = AppConstantsUtil.DEFAULT_HIGHLIGH_MOD;
      const lang = bookMeta.language;
      await this.$store.dispatch('ReadingSettingsStore/initReadingSettings', {
        bookId: this.publicationId,
        audioSpeed,
        highlightMode,
        lang
      });
    },
    async $_updateUserActivity() {
      const isEmptyReadingPosition = this.$store.getters[
        'ProgressStore/isEmptyReadingPosition'
      ]({
        publicationId: this.publicationId,
        type: this.publicationType
      });
      if (!isEmptyReadingPosition) {
        await this.$store.dispatch('ProgressStore/saveUserActivity', {
          publicationId: this.publicationId,
          type: this.publicationType
        });
      }
      if (this.limitedAccess && !isEmptyReadingPosition) {
        this.$_resetProgress();
      }
    },
    $_setScrubberPosition() {
      const audio = Array.from(document.querySelectorAll('audio') || []).find(
        audioEl => !audioEl.paused && audioEl.currentTime > 0 && !audioEl.ended
      );
      if (audio) {
        const currentTime = audio.currentTime * 1000;
        this.$store.commit('PlaybackStore/setIsPlaying', true);
        this.$store.commit('PlaybackStore/setCurrentTime', currentTime);
      }
    },

    async $_initMaterials() {
      const isCompilation =
        this.publicationType === PublicationsTypesEnum.COMPILATION;
      if (!this.publicationId || isCompilation) {
        return;
      }
      const meta = this.$store.getters['PublicationStore/getMeta'](
        this.publicationId
      );
      const store = this.$store;
      if (!meta) {
        return;
      }
      try {
        await PublicationLogicService.loadUserMaterials(this.$store);
        await store.dispatch('RecentBookStore/updateRecentBookItems', {
          meta: meta,
          bookId: this.publicationId
        });
      } catch (error) {
        logger.fatal('get error on open book:', error.stack);
      }
    },
    async $_playAndThenScrollToPara(paraId) {
      await this.$store.dispatch('PlaybackStore/playParagraph', {
        paraId
      });
      await PublicationScrollLogicService.animatedScrollToPara(
        this.$store,
        paraId
      );
    },

    $_scrollToScrubber() {
      const scrubberLocator = Locator.deserialize(this.currentScrubberLocator);
      PublicationScrollLogicService.scrollByLocator(
        this.$store,
        this.$router,
        scrubberLocator
      );
    },

    async $_playAudioMetaBlock(paraId) {
      const isIosBrowser = this.$store.getters['ContextStore/isIosBrowser'];
      const isPageInDefaultScrollingMode = this.$store.getters[
        'ReadingSettingsStore/getIsPageInDefaultScrollingMode'
      ];
      const isEachLineInIosBrowser =
        isIosBrowser && !isPageInDefaultScrollingMode;

      try {
        this.$store.dispatch('PlaybackStore/prepareAudio', {
          paraId
        });

        if (isIosBrowser) {
          await this.$_playAndThenScrollToPara(paraId);
          return;
        }

        await PublicationScrollLogicService.scrollToParaAndPlay(
          this.$store,
          this.$router,
          paraId,
          paraId
        );
      } catch (error) {
        logger.error(`Error while playing audio from metablock: ${error}`);
      } finally {
        if (isEachLineInIosBrowser) {
          this.$_scrollToScrubber();
        }

        this.$store.commit('PlaybackStore/setIsTriggeredFromMeta', false);
      }
    },

    floatingPlayButtonEventHandler(payload) {
      const { type } = payload;
      switch (type) {
        case FloatingPlayButtonEventEnum.PLAY_AUDIO:
          PublicationLogicService.startFromThirdOfScreenOrContinuePlay(
            this.$store,
            this.$router
          );
          break;
      }
    },

    $_addShortTapListener() {
      if (!this.$refs.presentPublication) {
        return;
      }
      this.$refs.presentPublication.addEventListener(
        'touchstart',
        this.addShortTapHandler
      );
      this.$refs.presentPublication.addEventListener(
        'touchmove',
        this.removeShortTapHandler
      );
    },

    $_trackPresentPublicationOpening() {
      const shouldTrackPresentPublication = this.$store.getters[
        'ContextStore/isDevice'
      ];

      if (!shouldTrackPresentPublication || !this.meta) {
        return;
      }

      const page_title = this.isCompilationOpen
        ? "User's compilation"
        : this.meta.name;
      const slug =
        this.$store.getters['LibraryStore/getPublicationSlugById'](
          this.publicationId
        ) || this.publicationId;
      const linkToTrack = `${this.clientReaderUrl}${slug}/`;

      // Note: For web this data is collected automatically by Google Analytics.
      // For Firebase Analytics (on devices) we need to send it manually.

      GoogleAnalyticsUtils.trackPageView(linkToTrack, page_title);
    },

    $_resetHighlight() {
      if (!this.contentHighlight || this.contentHighlight.isEmpty) {
        return;
      }
      this.$store.commit('OpenParameterStore/clearHighlight');
    },

    $_removeShortTapListener() {
      if (!this.$refs.presentPublication) {
        return;
      }
      this.$refs.presentPublication.removeEventListener(
        'touchstart',
        this.addShortTapHandler
      );
      this.$refs.presentPublication.removeEventListener(
        'touchmove',
        this.removeShortTapHandler
      );
    },

    addShortTapHandler() {
      if (this.$refs.presentPublication) {
        this.$refs.presentPublication.addEventListener(
          'touchend',
          this.shortTapHandler
        );
      }
    },

    removeShortTapHandler() {
      if (this.$refs.presentPublication) {
        this.$refs.presentPublication.removeEventListener(
          'touchend',
          this.shortTapHandler
        );
      }
    },

    $_updateMetaBlockInViewport(isMetaBlockInViewport) {
      this.$store.commit(
        'ContextStore/setIsMetaBlockInViewport',
        isMetaBlockInViewport
      );
    },

    $_updateStableContent(isStableContent) {
      this.$store.commit('ContextStore/setIsStableContent', isStableContent);
    },

    $_hideSearchInsideBookIfNeeded() {
      if (this.isBookSearchShown) {
        this.$store.commit('ContextStore/setIsBookSearchShown', false);
      }
    },

    searchNavigationEventHandler(payload) {
      const { type } = payload;
      switch (type) {
        case SearchNavigationEventEnum.CLOSE_SEARCH_NAV_BAR:
          this.$store.dispatch('BookStore/resetSearchNavigation', {
            bookId: this.publicationId
          });
          this.$_resetHighlight();
          this.$store.commit('ContextStore/setIsBookSearchShown', false);
          this.$store.commit('ContextStore/setIsFromSearchShown', false);
          break;
        case SearchNavigationEventEnum.NO_RESULTS_INSIDE_BOOK:
          this.$_resetHighlight();
          break;
        case SearchNavigationEventEnum.TEXT_CHANGED:
          this.$_resetHighlight();
          break;
        case SearchNavigationEventEnum.ERROR:
          this.$_presentPublicationErrorHandler(payload);
          break;
      }
    },

    $_preserveScrollPosition() {
      const scrollContainer = document.querySelector(this.scrollAreaSelector);
      if (!scrollContainer) {
        return;
      }

      this._savedScrollTop = scrollContainer.scrollTop;

      this.$nextTick(() => {
        requestAnimationFrame(() => {
          const currentScrollContainer = document.querySelector(
            this.scrollAreaSelector
          );
          if (currentScrollContainer && this._savedScrollTop !== undefined) {
            currentScrollContainer.scrollTop = this._savedScrollTop;
            this._savedScrollTop = undefined;
          }
        });
      });
    },

    async bookScrollEventHandler(payload) {
      const { type, data } = payload;
      switch (type) {
        case BookScrollEnum.DATA_FETCHED:
          this.setTransition(true);
          break;
        case BookScrollEnum.ON_ITEM_CLICK:
          this.itemClickEvent = data.event;
          break;
        case BookScrollEnum.OPEN_COMPILATION_EDIT_MENU:
          this.compilationEditMenuContext = data;
          this.shownCompilationEditMenu = true;
          break;
        case BookScrollEnum.ON_READING_AREA_CHANGE: {
          if (this.isTabChanging) {
            break;
          }

          if (data.isScrolling) {
            PublicationScrollLogicService.updateIsScrolling(this.$store, true);
          }
          this.$_updateMetaBlockInViewport(payload.data.isMetaBlockInViewport);

          if (data.isTooFar) {
            PublicationScrollLogicService.updateIsScrolling(this.$store, false);
          }
          if (!this.isAudioPlaying && this.needRepositionOnScroll) {
            this.$_restoreScrubberPosition();
          }
          if (
            this.isShownTextSelection &&
            !this.componentDisplayRules.selection
          ) {
            this.componentDisplayRules.selection = true;
          }
          const startLocator = this.contentHighlight?.selection?.start;
          const endLocator = this.contentHighlight?.selection?.end;
          if (
            data.isScrolling &&
            !this.showSearchNavigation &&
            startLocator &&
            endLocator &&
            !this.$_isInsideReadingArea(startLocator) &&
            !this.$_isInsideReadingArea(endLocator) &&
            this.isSamePublication
          ) {
            this.$_resetHighlight();
          }
          break;
        }
        case BookScrollEnum.ERROR:
        case BookScrollEnum.METABLOCK_ERROR:
        case AudioPlayerEvents.NETWORK_ERROR:
        case CustomErrorEnum.NETWORK_ERROR:
          this.$_presentPublicationErrorHandler(payload);
          break;
        case BookScrollEnum.MATERIAL_EVENT:
          this.materialEventHandler(data);
          break;
        case BookScrollEnum.START_PREP_PARA:
          break;

        case BookScrollEnum.REOPEN_ON_ROTATE: {
          PublicationNavigateLogicService.reopenOnRotateKeepScrubberInViewport(
            this.$store,
            this.$router
          );
          break;
        }
        case BookScrollEnum.STABLE: {
          this.emit(PresentPublicationEventsEnum.STABLE_SCROLL, data);
          this.$_updateMetaBlockInViewport(payload.data.isMetaBlockInViewport);
          this.$_updateStableContent(true);
          this.selectionTask = TextSelectionTaskEnum.INIT;

          this.$store.commit(
            'PublicationStore/setIsShownTextSelection',
            this.publicationType !== PublicationsTypesEnum.COMPILATION
          );
          this.hideProgressToolbar = false;
          this.$_restoreScrubberPosition();
          await this.$_showRestrictInformerIfNeed();

          this.$store.commit('PublicationStore/setIsFABDisabled', false);
          if (
            !this.initedOnStable &&
            !this.isGuest &&
            this.isOnline &&
            !this.isCompilationOpen
          ) {
            const {
              hasRemoteUpdate,
              paraId,
              requiredLocator
            } = await this.$store.dispatch('ProgressStore/loadReadingPosition');
            if (hasRemoteUpdate) {
              PublicationNavigateLogicService.openPublication(
                this.$store,
                this.$router,
                {
                  paragraphId: paraId,
                  requiredLocator
                }
              );
              this.$store.commit('PublicationStore/setIsScrollStabled', true);
              return;
            }
          }
          if (!this.initedOnStable) {
            await this.$_initMaterials();
            this.initedOnStable = true;
          }
          this.showAnnotationSidebar = true;
          this.emit(PresentPublicationEventsEnum.STABLE, data);
          PublicationScrollLogicService.updateIsScrolling(this.$store, false);
          this.$store.commit('PublicationStore/setIsScrollStabled', true);
          break;
        }
        case BookScrollEnum.COPY_EVENT:
          this.callCopy(data.event);
          break;
        case BookScrollEnum.SPACE_PRESSED_EVENT:
          this.$_processSpacePress(data.event);
          break;
        case BookScrollEnum.STOP:
          PublicationScrollLogicService.updateIsScrolling(this.$store, false);
          break;
        case BookScrollEnum.PLAY_PARA: {
          break;
        }
      }
      this.$emit('PresentPublicationEvent', payload);
    },
    async $_showRestrictInformerIfNeed() {
      const { indexDB } = await FeatureDetectorService.getDetections();
      const clientNodeContext = this.$store.getters[
        'ContextStore/clientNodeContextGetter'
      ];
      if (clientNodeContext.iFrameDetection) {
        return;
      }
      if (!indexDB) {
        this.$store.dispatch('ManagePopupStore/openToaster', {
          text: this.$t('App.restrict.info')
        });
      }
    },

    emit(type, data) {
      this.$emit('PresentPublicationEvent', {
        type,
        data
      });
    },
    annotationSidebarEventHandler(payload) {
      const { type, data } = payload;
      switch (type) {
        case AnnotationSidebarEvents.ERROR: {
          this.$_presentPublicationErrorHandler(payload);
          break;
        }
        case AnnotationSidebarEvents.OPEN_INFO_POPUP: {
          this.debouncedOpenAnnInfo(data);
          break;
        }
        default: {
          logger.warn(`Unsupported annotation sidebar type: ${type}`);
        }
      }
    },
    $_openAnnotationInfoPopup({ annotationIds, blockId, paraId }) {
      if (!annotationIds.length) {
        this.$store.dispatch('ManagePopupStore/openToaster', {
          text: this.$t('PresentPublication.error.infoPopup')
        });
        return;
      }

      if (this.isAnnInfoPopupOpened) {
        this.closeAnnInfoPopup();
      }

      if (
        this.showContextMenu ||
        this.isCreateAnnotationPopupOpen ||
        !annotationIds.length
      ) {
        return;
      }
      const lastAnnIndex = annotationIds.length - 1;
      const annElements = this.$refs.presentPublication.querySelectorAll(
        `.ann-${annotationIds[lastAnnIndex]}`
      );
      const firstAnnEl = annElements[0];
      firstAnnEl.classList.add('info-first-ann');
      const lastAnnEl = annElements[annElements.length - 1];
      lastAnnEl.classList.add('info-last-ann');

      this.$nextTick(() => {
        this.$store.dispatch('ManagePopupStore/openPopup', {
          name: PopupNamesEnum.ANNOTATION_INFO_POPUP,
          popupContext: {
            annotationIds,
            firstAttachableElementClass: 'info-first-ann',
            secondAttachableElementClass: 'info-last-ann',
            blockId,
            paraId,
            openFromExtras: false
          }
        });
      });
    },
    closeAnnInfoPopup() {
      this.$store.dispatch('ManagePopupStore/closePopup', {
        name: PopupNamesEnum.ANNOTATION_INFO_POPUP
      });
    },
    $_presentPublicationErrorHandler({ type, data }) {
      this.$store.dispatch('ManagePopupStore/finishLoader');
      const { error } = data;

      switch (type) {
        case BookScrollEnum.ERROR:
          this.componentDisplayRules.readingArea = false;
          this.emit(PresentPublicationEventsEnum.ERROR, data);
          break;
        case BookScrollEnum.METABLOCK_ERROR:
          if (error.name === CustomErrorEnum.EMPTY_DATA) {
            this.componentDisplayRules.metaBlock = false;
          }
          break;
        case AudioPlayerEvents.ERROR:
        case AudioPlayerEvents.NETWORK_ERROR:
        case CustomErrorEnum.NETWORK_ERROR: {
          this.$store.dispatch('PlaybackStore/pause');
          this.$store.commit('PlaybackStore/setCurrentParaId', null);
          break;
        }
        default:
          this.$_hideComponentByType(type);
      }

      if (type !== BookScrollEnum.ERROR) {
        this.$_showErrorToasterByType(type);
      }
    },
    $_showErrorToasterByType(type) {
      const messagesByType = {
        [BookScrollEnum.METABLOCK_ERROR]: this.$t(
          'PresentPublication.error.bookMeta'
        ),
        [ReadingAreaEvents.ERROR]: this.$t(
          'PresentPublication.error.readingArea'
        ),
        [PositionTrackerEvents.ERROR]: this.$t(
          'PresentPublication.error.positionTracker'
        ),
        [SelectionEvents.ERROR]: this.$t('PresentPublication.error.selection'),
        [CompilationEditToolbarEvents.ERROR]: this.$t(
          'PresentPublication.error.compilationEditToolbar'
        ),
        [AnnotationInfoPopupEvents.ERROR]: this.$t(
          'PresentPublication.error.annInfoPopup'
        ),
        [SearchNavigationEventEnum.ERROR]: this.$t(
          'PresentPublication.error.searchNavigation'
        ),
        [MenuEventEnum.ERROR]: this.$t('PresentPublication.error.contextMenu'),
        [AnnotationSidebarEvents.ERROR]: this.$t(
          'PresentPublication.error.annSidebar'
        ),
        [ParaNoteContextMenuEvents.ERROR]: this.$t(
          'PresentPublication.error.paraNotes'
        ),
        [EssayTaskPopupEvents.ERROR]: this.$t('PresentPublication.error.essay'),
        [AudioPlayerEvents.ERROR]: this.$t(
          'PresentPublication.error.audioPlayer'
        ),
        [AnnotationAudioEvents.ERROR]: this.$t(
          'PresentPublication.error.annotationAudio'
        ),
        [CustomErrorEnum.NETWORK_ERROR]: this.$t(
          'PublicationAudioManager.audio.mediaErrorConnectionError'
        ),
        [AudioPlayerEvents.NETWORK_ERROR]: this.$t(
          'PublicationAudioManager.audio.mediaErrorConnectionError'
        ),
        [ProgressToolbarEvents.ERROR]: this.$t(
          'PresentPublication.error.progressToolbar'
        )
      };

      const networkErrorTypes = [
        CustomErrorEnum.NETWORK_ERROR,
        AudioPlayerEvents.NETWORK_ERROR
      ];

      const text = messagesByType[type];
      const payload = {
        text
      };

      if (networkErrorTypes.includes(type)) {
        payload.toasterIcon = 'ico-no-wifi';
        payload.toasterClose = false;
      }

      this.$store.dispatch('ManagePopupStore/openErrorToaster', {
        payload
      });
    },
    $_hideComponentByType(type) {
      const componentDisplayPropsByType = {
        [ReadingAreaEvents.ERROR]: 'readingArea',
        [PositionTrackerEvents.ERROR]: 'positionTracker',
        [SelectionEvents.ERROR]: 'selection',
        [MenuEventEnum.ERROR]: 'contextMenu',
        [AnnotationSidebarEvents.ERROR]: 'annSidebar',
        [CompilationEditToolbarEvents.ERROR]: 'compEditToolbar',
        [AnnotationInfoPopupEvents.ERROR]: 'annInfoPopup',
        [SearchNavigationEventEnum.ERROR]: 'searchNavigation',
        [ParaNoteContextMenuEvents.ERROR]: 'paraNoteContextMenu',
        [EssayTaskPopupEvents.ERROR]: 'essayTaskPopup',
        [TestEditorEvents.ERROR]: 'testEditor',
        // [AudioPlayerEvents.ERROR]: 'audioPlayer',
        [AnnotationAudioEvents.ERROR]: 'annotationAudio',
        [ProgressToolbarEvents.ERROR]: 'progressToolbar'
      };

      const componentDisplayProp = componentDisplayPropsByType[type];
      this.componentDisplayRules[componentDisplayProp] = false;
    },
    materialEventHandler(payload) {
      switch (payload.materialType) {
        case MaterialTypes.PARA_NOTE: {
          this.$_paraNoteEventHandler(payload);
          break;
        }
        case MaterialTypes.ESSAY_TASK: {
          this.$_essayTaskEventHandler(payload);
          break;
        }
        case MaterialTypes.ESSAY: {
          this.$_essayEventHandler(payload);
          break;
        }
        case MaterialTypes.QUIZ: {
          this.$_quizEventHandler(payload);
          break;
        }
        case MaterialTypes.FLASHCARD: {
          this.$_flashcardEventHandler(payload);
          break;
        }
        default: {
          logger.warn(
            'Unsupported material type in Present publication: ' +
              payload.materialType
          );
        }
      }
    },
    $_paraNoteEventHandler(payload) {
      const data = payload.materialData;
      const eventData = payload.eventData;

      this.paraNoteMenuParams.setEditMode();
      this.paraNoteMenuParams.changeParagraphId(data.paragraphId);
      this.paraNoteMenuParams.changeParagraphNote(data);
      this.paraNoteMenuParams.changeMenuPosition({
        top: eventData.y,
        left: eventData.x
      });
      this.showParaNoteContextMenu = true;
    },
    $_essayEventHandler(payload) {
      switch (payload.task) {
        case MenuTasks.CHANGE: {
          this.$store.dispatch('MaterialsStore/changeEssayText', {
            essay: payload.materialData
          });
          break;
        }
        case MenuTasks.ADD: {
          this.$store.dispatch('MaterialsStore/completeEssay', {
            essay: payload.materialData
          });
          break;
        }
      }
    },
    $_essayTaskEventHandler(payload) {
      switch (payload.task) {
        case MenuTasks.DELETE: {
          const label = this.$t('Essay.label');
          const data = payload.materialData;
          const type = MaterialTypes.ESSAY_TASK;
          this.$_openConfirmationPopup({ label, data, type });
          break;
        }
        case MenuTasks.CHANGE: {
          this.$_openEssayTaskPopup(payload.materialData);
          break;
        }
        case MenuTasks.ADD: {
          // OPEN HERE ESSAYTASK CONTEXT MENU
          break;
        }
      }
    },
    $_quizEventHandler(payload) {
      switch (payload.task) {
        case MenuTasks.DELETE: {
          const label = this.$t('ManageTest.Quiz.Label');
          const data = payload.materialData;
          const type = MaterialTypes.QUIZ;
          this.$_openConfirmationPopup({ label, data, type });
          break;
        }
      }
    },
    $_flashcardEventHandler(payload) {
      switch (payload.task) {
        case MenuTasks.DELETE: {
          const label = this.$t('ManageTest.Flashcard.Label');
          const data = payload.materialData;
          const type = MaterialTypes.FLASHCARD;
          this.$_openConfirmationPopup({ label, data, type });
          break;
        }
      }
    },
    $_openConfirmationPopup({ label, data, type }) {
      let title = this.$_createConfirmationMessage(label);
      let message = null;
      let contentImage = null;
      let primaryLabel = null;

      switch (type) {
        case CompilationSelectionTypes.SELECTION:
          title = this.$t(
            'CompilationDeleteContentItemPopup.contentTitle.selection.label'
          );
          message = this.$t(
            'CompilationDeleteContentItemPopup.contentMessage.label'
          );
          contentImage = images.picGarbage;
          primaryLabel = this.$t('Popup.button.delete.label');
          break;
        default:
          break;
      }
      this.$store.dispatch('ManagePopupStore/openPopup', {
        name: PopupNamesEnum.CONFIRM_POPUP,
        popupContext: {
          title,
          message,
          contentImage,
          primaryLabel,
          type,
          data
        }
      });
    },
    $_openParagraphSharePopup({ compilationId, paraId }) {
      const linkToShare = this.$store.getters[
        'CompilationsStore/getSelectionLinkToShare'
      ]({
        compilationId,
        paraId
      });
      this.$store.dispatch('ManagePopupStore/openPopup', {
        name: PopupNamesEnum.SOCIAL_SHARING_POPUP,
        popupContext: { linkToShare }
      });
    },
    $_createConfirmationMessage(label) {
      return this.$t('PopupDelete.content.text', {
        obj: label
      });
    },
    async callCopy() {
      const copyRange = Locator.deserialize(this.selectedLocator.toJSON());
      const isSafari = this.$store.getters['ContextStore/isSafari'];
      const isIos = this.$store.getters['ContextStore/isIos'];
      if (!isSafari && !isIos) {
        await this.$store.dispatch('TextCopyStore/getShortenLink', {
          locator: copyRange,
          bookId: this.publicationId
        });
      }

      let executed = null;
      if (isSafari) {
        if (navigator?.clipboard && navigator.clipboard?.writeText) {
          executed = await navigator.clipboard.writeText(
            copyRange?.text?.highlight
          );
        } else {
          executed = document.execCommand('Copy');
        }
      }

      if (!executed) {
        const event = {};
        const allowCopy = true;
        this.$_copyText(event, allowCopy, copyRange);
      }
    },
    $_unsubscribe() {
      document.removeEventListener('copy', this.$_copyText);
      if (this.subscribeAction) {
        this.subscribeAction();
      }
    },
    $_subscribe() {
      document.addEventListener('copy', this.$_copyText);
      if (this.$refs.presentPublication) {
        this.$refs.presentPublication.addEventListener(
          'mousemove',
          this.$_hidePointerWhenInactive
        );
        this.$refs.presentPublication.addEventListener(
          'mousedown',
          this.$_clearNativeSelection
        );
      }

      if (this.$store.getters['ContextStore/isDevice']) {
        window.plugins.insomnia.keepAwake();
      }

      this.subscribeAction = this.$store.subscribeAction(action => {
        switch (action.type) {
          case 'ManagePopupStore/openPopup':
            if (
              [
                PopupNamesEnum.READING_SETTINGS_POPUP,
                PopupNamesEnum.READING_SETTINGS_BOTTOM_POPUP,
                PopupNamesEnum.EXTRAS_POPUP,
                PopupNamesEnum.APP_MENU_WRAPPER,
                PopupNamesEnum.RECENT_BOOKS_WRAPPER
              ].includes(action.payload.name)
            ) {
              this.$_removeBookSelectionAndOverlays();
            }

            if (action.payload.name === PopupNamesEnum.ESSAY_TASK_POPUP) {
              this.$_openEssayTaskPopup(action.payload.popupContext.data);
            }

            if (action.payload.name === PopupNamesEnum.TEST_EDITOR) {
              this.$_openTestEditor(action.payload.id);
            }

            break;
          case 'ReadingSettingsStore/setFont':
          case 'ReadingSettingsStore/setExpandedNotesMode':
          case 'ReadingSettingsStore/setExpandedFootnotesMode':
          case 'ReadingSettingsStore/decreaseFontSize':
          case 'ReadingSettingsStore/increaseFontSize':
          case 'ReadingSettingsStore/decreaseLineSpacing':
          case 'ReadingSettingsStore/increaseLineSpacing':
            this.$_removeBookSelectionAndOverlays();
            this.$_moveScrubberToCorrectLocator();
            break;
          case 'ReadingSettingsStore/clearSelection':
            this.$_resetSelectionAndHideContextMenu(action.payload);
            break;
          case 'ManagePopupStore/closePopup': {
            if (action.payload.name === PopupNamesEnum.ANNOTATION_INFO_POPUP) {
              this.removeActiveChipClass();
              return;
            }

            const popupNames = [
              PopupNamesEnum.TYPO_POPUP,
              PopupNamesEnum.CREATE_COMPILATION_POPUP,
              PopupNamesEnum.SOCIAL_SHARING_POPUP,
              PopupNamesEnum.READING_SETTINGS_POPUP,
              PopupNamesEnum.READING_SETTINGS_BOTTOM_POPUP,
              PopupNamesEnum.EXTRAS_POPUP
            ];

            if (action.payload?.data?.skipClosing) {
              return;
            }

            if (popupNames.includes(action.payload.name)) {
              if (!this.isAudioPlaying) {
                this.$_restoreScrubberPosition();
              }
              this.$_removeBookSelectionAndOverlays();
            }
            break;
          }
          case 'CategoriesStore/addCategory':
          case 'CategoriesStore/changeCategory':
            this.$_injectNewStyles(action.payload.category);
            break;
          case 'PlaybackStore/playAudioFromMetaBlock':
            this.$_playAudioMetaBlock(action.payload.paraId);
            break;
          case 'PublicationStore/resetSelectionTrigger':
            this.$_resetSelection();
            break;
          case 'ManagePopupStore/updatePopupContext':
            this.$_removeBookSelectionAndOverlays();
            break;
        }
      });

      this.subscribeMutation = this.$store.subscribe(({ type, payload }) => {
        switch (type) {
          case 'MediaDetectorStore/setMedia':
            this.changeMediaHandler(payload);
            break;
          case 'ContextStore/setIsProgressbarCompact':
          case 'ContextStore/setIsBookSearchShown':
            this.$_removeBookSelectionAndOverlays();
        }
      });
    },
    async changeMediaHandler(payload) {
      if (payload?.isKeyboardMayBeOpen) {
        return;
      }
      this.mediaChange = true;
      this.$_removeBookSelectionAndOverlays();
      await PromiseUtil.wait(this.SUBSCRIBE_MEDIA_TRIGGERED_TIME);
      this.mediaChange = false;
    },
    async removeActiveChipClass() {
      await PromiseUtil.wait(200);
      if (this.isAnnInfoPopupOpened) {
        return;
      }
      this.$store.dispatch('AnnotationsStore/setActiveAnnIds', {});
    },
    $_moveScrubberToCorrectLocator() {
      this.$store.dispatch('PlaybackStore/rerenderScrubber');
    },
    $_injectNewStyles(category) {
      const styleContainer = window.document.head;
      this.$store.dispatch('CategoriesStore/injectCategoryStyle', {
        category,
        styleContainer
      });
    },
    async onVisibilityChange() {
      const isTriggeredFromMeta = this.$store.getters[
        'PlaybackStore/getIsTriggeredFromMeta'
      ];
      if (this.isWebBackground && isTriggeredFromMeta) {
        PublicationScrollLogicService.stopAnimateScroll(this.$store);
        const paraId = this.$store.getters[
          'PublicationStore/getFirstAudioParagraph'
        ]();
        PublicationScrollLogicService.scrollImmediatelyToPara(
          this.$store,
          paraId
        );
        const isAudioPlaying = this.$store.getters[
          'PlaybackStore/isAudioPlaying'
        ];
        if (!isAudioPlaying) {
          this.$store.dispatch('PlaybackStore/playParagraph', {
            paraId: paraId
          });
          const preloadCount = this.$store.getters[
            'PublicationStore/geTotalContentItemCount'
          ];
          await this.$store.dispatch('PlaybackStore/preloadAlignment', {
            paraId,
            bookId: this.publicationId,
            preloadCount: preloadCount
          });
        }
      }

      if (this.isWebBackground || !this.isAudioPlaying) {
        return;
      }

      const scrubberLocator = Locator.deserialize(this.currentScrubberLocator);
      PublicationScrollLogicService.scrollByLocator(
        this.$store,
        this.$router,
        scrubberLocator
      );
      this.$_setIsOutOfBackground();
      this.$store.dispatch('PlaybackStore/showScrubberByLocator', {
        bookId: this.publicationId,
        InTextLocator: scrubberLocator.startLocator
      });
    },
    async selectionEventHandler(payload) {
      const { type, data } = payload;
      switch (type) {
        case SelectionEvents.START_SELECTION: {
          this.$_startSelectionHandler();
          break;
        }
        case SelectionEvents.PROCESS_SELECTION: {
          this.selectionTask = TextSelectionTaskEnum.PROCESS_SELECTION;
          if (this.isDictionaryAutoOpen && this.isDictionaryPopupOpened) {
            this.$_closeDictionaryPopup();
          }
          this.$_hideContextMenu(0);
          break;
        }
        case SelectionEvents.END_SELECTION: {
          this.$_endSelectionHandler(data);
          break;
        }
        case SelectionEvents.ERROR: {
          this.$_presentPublicationErrorHandler(payload);
          break;
        }
        default: {
          logger.error(`Unsupported selection event type: ${type}`);
        }
      }
    },
    stopPlayingAnn() {
      this.$store.dispatch('AnnotationsStore/stopPlayingAnnotation');
    },
    $_startSelectionHandler() {
      if (!this.isDictionaryAutoOpen && this.isDictionaryPopupOpened) {
        this.$_closeDictionaryPopup();
      }
      if (this.getPlayingAnnId) {
        this.stopPlayingAnn();
      }
      if (this.isExtrasOpened && !this.isWide) {
        this.$store.dispatch('ManagePopupStore/closePopup', {
          name: PopupNamesEnum.EXTRAS_POPUP
        });
      }
    },
    $_setSelectedLocator(locator) {
      this.$store.dispatch('MaterialsStore/setSelectedLocator', { locator });
    },
    async $_endSelectionHandler({ locator }) {
      this.selectionTask = TextSelectionTaskEnum.NONE;
      this.$_setSelectedLocator(locator);
      const lastParaElement = MarkerUtils.getParaByRangeLocator(locator);
      if (!locator || !lastParaElement) {
        this.$_resetSelectionAndHideContextMenu();
        this.$store.dispatch('WikiStore/cancelAllRequests');
        return;
      }
      this.$_updateContextMenu(lastParaElement);
      this.$_showContextMenuIfNeeded();
      this.$_searchShortDefinition();
      this.$_searchExploreDefinition(lastParaElement);
      this.$_setContextMenuLocator();
      this.$store.dispatch('PlaybackStore/pause');
    },
    $_showContextMenuIfNeeded() {
      const shouldShow =
        !this.isCreateAnnotationPopupOpen &&
        !this.showParaNoteContextMenu &&
        !this.isAnnInfoPopupOpened;
      if (shouldShow) {
        this.showContextMenu = true;
      }
    },
    async $_searchExploreDefinition(selectedPara) {
      const paraId = this.selectedLocator?.startLocator?.prefixedParagraphId;
      if (!paraId || !selectedPara) {
        return;
      }
      const sentenceLanguage = this.$store.getters['BookStore/getParaLanguage'](
        paraId
      );
      const translatorLanguage = this.$store.getters[
        'WikiStore/getTranslatorLanguage'
      ];
      this.$store.dispatch('WikiStore/createExploreData', {
        publicationId: this.publicationId,
        selectedLocator: this.selectedLocator,
        selectedPara,
        sentenceLanguage,
        translatorLanguage
      });
    },
    async $_searchShortDefinition() {
      let locator = this.selectedLocator;
      if (!this.isOnline || !locator) {
        return;
      }

      if (
        this.selectedLocator.startLocator.prefixedParagraphId !==
        this.selectedLocator.endLocator.prefixedParagraphId
      ) {
        locator = this.$store.getters[
          'PublicationStore/getLocatorFromStartToParaEnd'
        ](locator, this.publicationId);
      }

      const selectedText = TextUtils.extractContentByRangeLocator(locator);
      if (!selectedText) {
        return;
      }
      this.$store.commit('WikiStore/setCurrentText', selectedText.trim());
    },
    $_setContextMenuLocator() {
      const paraId = this.selectedLocator.startLocator.prefixedParagraphId;
      this.paraNoteMenuParams.changeParagraphId(paraId);
      this.essayTaskPopupParams.changeParagraphId(paraId);
      this.testEditorParams.changeParagraphId(paraId);
    },
    $_hidePointerWhenInactive() {
      this.$_resetHidingPointer();
      this.hidePointerTimeout = setTimeout(this.$_hidePointer, 5000);
    },
    $_hidePointer() {
      this.isPointerHidden = true;
    },
    $_resetHidingPointer() {
      this.isPointerHidden = false;
      clearTimeout(this.hidePointerTimeout);
    },
    $_updateContextMenu(lastParaElement) {
      const paraId = this.selectedLocator.endLocator.prefixedParagraphId;
      const isHeader = MarkerUtils.isHeader(lastParaElement);
      const isMultipleParaSelected = !this.selectedLocator.startLocator.equalsByBasis(
        this.selectedLocator.endLocator
      );
      const isMaxParaSelected =
        this.selectedLocator.endLocator._paragraphNumber -
          this.selectedLocator.startLocator._paragraphNumber +
          1 >
        AppConstantsUtil.MAX_SELECTION_PARAS_NUMBER;

      const isElementVoiced = this.$store.getters['BookStore/isElementVoiced'](
        this.publicationId,
        paraId
      );
      // todo: @thre update selector, when ILM-1259 is done.
      const isDescriptionSelected = false;
      this.menuOpenContext
        .changeIsHeader(isHeader)
        .changeIsMultipleParaSelected(isMultipleParaSelected)
        .changeIsMaxParaSelected(isMaxParaSelected)
        .changeIsDescriptionSelected(isDescriptionSelected)
        .changeIsElementVoiced(isElementVoiced);
    },
    compilationMenuEventHandler({ type, data }) {
      switch (type) {
        case PopupEventEnum.CLOSE:
          this.shownCompilationEditMenu = false;
          break;
        case CompilationEventsEnum.DUPLICATE_SELECTION:
          this.$store.dispatch('CompilationsStore/duplicateSelection', {
            compilationId: this.publicationId,
            paraId: data.paraId
          });
          this.shownCompilationEditMenu = false;
          break;
        case CompilationEventsEnum.REMOVE:
          this.$_openConfirmationPopup({
            type: CompilationSelectionTypes.SELECTION,
            data: { compilationId: this.publicationId, paraId: data.paraId },
            label: this.$t('Compilation.selectionTypes.selection')
          });
          this.shownCompilationEditMenu = false;
          break;
        case CompilationEventsEnum.SHARE_SELECTION: {
          this.$_openParagraphSharePopup({
            compilationId: this.publicationId,
            paraId: data.paraId
          });
          this.shownCompilationEditMenu = false;
          break;
        }
      }
    },
    async contextMenuHandler(payload) {
      const { type, data } = payload;

      switch (type) {
        case MenuEventEnum.REOPEN:
          PublicationNavigateLogicService.openPublication(
            this.$store,
            this.$router,
            {
              paragraphId: data.paraId
            }
          );
          break;
        case MenuEventEnum.CLOSE: {
          if (this.mediaChange) {
            return;
          }
          if (this.isDictionaryAutoOpen && this.isDictionaryPopupOpened) {
            this.$_closeDictionaryPopup();
          }
          this.$_resetSelectionAndHideContextMenu();
          break;
        }
        case MenuEventEnum.READY_ELEMENT_POSITION: {
          this.$_setContextMenuPosition(data);
          if (
            this.isDictionaryAutoOpen &&
            !this.isDictionaryPopupOpened &&
            this.isAtLeastOneTabOn()
          ) {
            this.$_openDictionaryPopup();
          }
          break;
        }
        case MenuEventEnum.ADD_PARA_NOTE: {
          this.$_openParaNoteContextMenu();
          break;
        }
        case MenuEventEnum.ADD_ESSAY_TASK: {
          this.$_openEssayTaskPopup();
          break;
        }
        case MenuEventEnum.ADD_QUIZ_TASK: {
          this.$_openQuizEditor();
          break;
        }
        case MenuEventEnum.ADD_FLASHCARD_TASK: {
          this.$_openFlashcardEditor();
          break;
        }
        case MenuEventEnum.OPEN_CORRECTION_LINK: {
          this.$_openCorrectionLinkBySelection(this.selectedLocator);
          break;
        }
        case MenuEventEnum.ERROR: {
          this.$_presentPublicationErrorHandler(payload);
          break;
        }
      }
    },
    $_removeBookSelectionAndOverlays() {
      if (this.isDictionaryAutoOpen && this.isDictionaryPopupOpened) {
        this.$_closeDictionaryPopup();
      }
      this.$_removeSelection();
      this.$_hideContextMenu();
    },
    $_resetSelectionAndHideContextMenu(delayTime = 150) {
      this.$_resetSelection();
      this.$_hideContextMenu(delayTime);
    },
    $_resetSelection() {
      if (
        !this.selectedLocator ||
        this.selectionTask === TextSelectionTaskEnum.PROCESS_SELECTION
      ) {
        return;
      }
      this.$_removeSelection();
    },
    $_removeSelection() {
      this.selectionTask = TextSelectionTaskEnum.RESET_SELECTION;
      this.$nextTick(() => {
        this.selectionTask = TextSelectionTaskEnum.NONE;
      });
    },
    isAtLeastOneTabOn() {
      return (
        this.readingSettingsView.googleTranslate ||
        this.readingSettingsView.dictionaryMode ||
        this.readingSettingsView.wikiMode
      );
    },
    $_openDictionaryPopup() {
      this.$store.dispatch('ManagePopupStore/openPopup', {
        name: PopupNamesEnum.DICTIONARY_POPUP,
        popupContext: {}
      });
    },
    $_closeDictionaryPopup(skipClosing = true) {
      this.$store.dispatch('ManagePopupStore/closePopup', {
        name: PopupNamesEnum.DICTIONARY_POPUP,
        data: {
          skipClosing
        }
      });
    },
    $_openEditAnnPopup(data) {
      this.$store.dispatch('PlaybackStore/pause');
      const menuPosition = ContextMenuParamsFactory.createMenuPosition();
      this.$store.dispatch('ManagePopupStore/openPopup', {
        name: PopupNamesEnum.CREATE_ANNOTATION_POPUP,
        popupContext: {
          annotation: data?.annotation || null,
          menuPosition
        }
      });
    },
    $_setContextMenuPosition(position) {
      this.paraNoteMenuParams.changeMenuPosition(position);
    },
    async $_copyText(event = {}, allowCopy, copyTextRange) {
      const tagName = get(event, 'target.tagName', null);
      const nodeName = get(event, 'currentTarget.body.nodeName', null);
      const isBody = [tagName, nodeName].includes('BODY');
      const isCopyButton =
        event.currentTarget &&
        event.currentTarget.classList &&
        event.currentTarget.classList.contains('context-menu-item');
      const isInTextEditor = event.target?.closest?.('#quill-container');

      if (isInTextEditor) {
        return;
      }

      if (isBody || isCopyButton || allowCopy) {
        const isSafari = this.$store.getters['ContextStore/isSafari'];
        const isIos = this.$store.getters['ContextStore/isIos'];
        if (isSafari || isIos) {
          await this.$store.dispatch('TextCopyStore/getShortenLink', {
            locator: copyTextRange,
            bookId: this.publicationId
          });
        }
        if (copyTextRange) {
          this.$store.dispatch('TextCopyStore/copySelectedText', {
            locator: copyTextRange,
            bookId: this.publicationId,
            event
          });
        }

        this.$_resetSelectionAndHideContextMenu();
      }
    },
    async $_processSpacePress(event) {
      const shouldProcessSpaceClick = document.activeElement === document.body;
      if (!shouldProcessSpaceClick) {
        return;
      }
      event.preventDefault();
      if (this.isAudioPlaying) {
        this.$store.dispatch('PlaybackStore/pause');
        return;
      }
      PublicationLogicService.startFromThirdOfScreenOrContinuePlay(
        this.$store,
        this.$router
      );
    },

    async $_hideContextMenu(delayTime = 150) {
      if (!this.showContextMenu) {
        return;
      }
      await PromiseUtil.wait(delayTime);
      this.showContextMenu = false;
      if (!this.$store.state.PlaybackStore.displayScrubber) {
        this.$_restoreScrubberPosition();
      }
    },
    $_openParaNoteContextMenu() {
      this.paraNoteMenuParams.setAddMode();
      this.showParaNoteContextMenu = true;
    },
    $_openEssayTaskPopup(essay) {
      if (!essay) {
        this.essayTaskPopupParams.setAddMode();
      } else {
        this.essayTaskPopupParams
          .setEditMode()
          .changeEssay(essay)
          .changeParagraphId(essay.paragraphId);
      }
      this.$_resetSelection();
      this.showEssayTaskPopup = true;
    },
    async $_openQuizEditor() {
      this.testEditorParams.setQuizType();
      const testId = await this.$store.dispatch('MaterialsStore/addQuiz', {
        bookId: this.materialsId,
        paraId: this.testEditorParams.paragraphId
      });
      this.$_openTestEditor(testId);
    },
    async $_openFlashcardEditor() {
      this.testEditorParams.setFlashcardType();
      const testId = await this.$store.dispatch('MaterialsStore/addFlashcard', {
        bookId: this.materialsId,
        paraId: this.testEditorParams.paragraphId
      });
      this.$_openTestEditor(testId);
    },
    $_openCorrectionLinkBySelection(selectedLocator) {
      const meta = this.$store.getters['PublicationStore/getMeta']();
      this.launchIlmEditor(meta?.ilmId, selectedLocator);
    },
    launchIlmEditor(bookId, selectedLocator) {
      const url = this.$store.getters['BookStore/getFullIlmUrl'](
        bookId,
        selectedLocator
      );
      this.$store.dispatch('ContextStore/openUrl', { url });
    },
    async $_openTestEditor(testId) {
      this.$_resetSelection();
      this.testEditorParams.changeTestId(testId);
      this.showTestEditor = true;
    },
    paraNoteMenuEventHandler(payload) {
      const type = payload.type;
      switch (type) {
        case PopupEventEnum.CLOSE:
          this.$_closeParaNoteContextMenu();
          break;

        case ParaNoteContextMenuEvents.ERROR:
          this.$_closeParaNoteContextMenu();
          this.$_presentPublicationErrorHandler(payload);
          break;

        default:
          logger.warn(`Unsupported paraNoteContextMenu event type: ${type}`);
      }
    },
    $_closeParaNoteContextMenu() {
      this.$_resetSelection();
      this.showParaNoteContextMenu = false;
    },
    essayTaskPopupEventHandler(payload) {
      const type = payload.type;
      switch (type) {
        case PopupEventEnum.CLOSE:
          this.$_closeEssayTaskPopup();
          break;
        case EssayTaskPopupEvents.ERROR:
          this.$_presentPublicationErrorHandler(payload);
          break;
        default:
          logger.warn(`Unsupported essayTaskPopup event type: ${type}`);
      }
    },
    $_closeEssayTaskPopup() {
      this.showEssayTaskPopup = false;
      this.$store.dispatch('ManagePopupStore/closePopup', {
        name: PopupNamesEnum.ESSAY_TASK_POPUP
      });
    },
    testEditorEventHandler(payload) {
      const type = payload.type;
      switch (type) {
        case PopupEventEnum.CLOSE:
          this.$_closeTestEditor();
          break;

        case TestEditorEvents.ERROR:
          this.$_presentPublicationErrorHandler(payload);
          break;

        default:
          logger.warn(`Unsupported testEditor event type: ${type}`);
      }
    },
    $_closeTestEditor() {
      this.showTestEditor = false;
    },
    audioPlayerEventHandler(payload) {
      const type = payload.type;
      switch (type) {
        case AudioPlayerEvents.ERROR:
        case AudioPlayerEvents.NETWORK_ERROR:
          this.$_presentPublicationErrorHandler(payload);
          break;
        case AudioPlayerEvents.MOUNTED:
          break;
        default:
          logger.warn(`Unsupported audioPlayer event type: ${type}`);
      }
    },
    annotationAudioHandler(payload) {
      switch (payload.type) {
        case AnnotationAudioEvents.ERROR:
          this.$_presentPublicationErrorHandler(payload);
          break;
        default:
          logger.warn(`Unsupported annotationAudio event: ${payload.type}`);
      }
    },
    compilationEditToolbarEventHandler(payload) {
      const { type } = payload;
      switch (type) {
        case CompilationEditToolbarEvents.ERROR:
          this.$_presentPublicationErrorHandler(payload);
          break;
      }
    },
    async $_restoreScrubberPosition() {
      const InTextLocator = this.$store.getters[
        'ProgressStore/getCurrentAudioPosition'
      ]({ type: this.publicationType, publicationId: this.publicationId });
      if (!(InTextLocator instanceof Locator.InTextLocator)) {
        return Promise.resolve();
      }
      const isInsideReadingArea = this.$_isInsideReadingArea(InTextLocator);

      if (!isInsideReadingArea) {
        return Promise.resolve();
      }
      this.$store.dispatch('PlaybackStore/hideScrubber');
      await this.$nextTick();
      await this.$store.dispatch('PlaybackStore/showScrubberByLocator', {
        bookId: this.publicationId,
        InTextLocator
      });
    },

    $_isInsideReadingArea(inTextLocator) {
      return this.$store.getters['ProgressStore/isInsideReadingArea'](
        inTextLocator
      );
    },

    $_setIsOutOfBackground() {
      this.isOutOfBackground = true;
      clearTimeout(this.isOutOfBgTimeout);
      this.isOutOfBgTimeout = setTimeout(() => {
        this.isOutOfBackground = false;
      }, 3000);
    },

    debouncedOpenAnnInfo: debounce(function(params) {
      if (this.contentHighlight.isEmpty) {
        this.$_openAnnotationInfoPopup(params);
      }
    }, DEBOUNCE_TIME),

    stopPlayIfOutOfReadingArea() {
      const isPageInDefaultScrollingMode = this.$store.getters[
        'ReadingSettingsStore/getIsPageInDefaultScrollingMode'
      ];
      if (
        isPageInDefaultScrollingMode ||
        !this.currentScrubberLocator ||
        !this.isAudioPlaying ||
        this.isAppInBackground ||
        this.isWebBackground
      ) {
        return;
      }
      const locator = Locator.deserialize(this.currentScrubberLocator);
      const isInsideReadingArea = this.$_isInsideReadingArea(locator);

      if (isInsideReadingArea) {
        return;
      }
      if (this.isAndroid && this.isOutOfBackground) {
        return;
      }

      const isTriggeredFromMeta = this.$store.getters[
        'PlaybackStore/getIsTriggeredFromMeta'
      ];
      if (isTriggeredFromMeta) {
        return;
      }

      this.$store.dispatch('PlaybackStore/pause');
    },
    $_reinitPlaybackStore() {
      this.$store.dispatch('PlaybackStore/pause');
      this.$store.dispatch('PlaybackStore/reinit');
    },
    $_clearReadingSettings() {
      this.$store.dispatch('ReadingSettingsStore/clearReadingSettings');
    },
    $_clearMaterials() {
      this.$store.dispatch('MaterialsStore/clearMaterials');
    },
    progressToolbarEventHandler(payload) {
      const { type } = payload;
      switch (type) {
        case ProgressToolbarEvents.ERROR:
          this.$_presentPublicationErrorHandler(payload);
          break;
      }
    },
    positionTrackerEventHandler(payload) {
      const type = payload.type;
      switch (type) {
        case PositionTrackerEvents.ERROR:
          this.$_presentPublicationErrorHandler(payload);
          break;
        default:
          logger.warn(`Unsupported positionTracker event type: ${type}`);
      }
    },

    audioTrackerEventHandler(payload) {
      const type = payload.type;
      switch (type) {
        case AudioTrackerEvents.ERROR:
          this.$_presentPublicationErrorHandler(payload);
          break;
        default:
          logger.warn(`Unsupported audio tracker event type: ${type}`);
      }
    },

    readingAreaEventHandler(payload) {
      const type = payload.type;
      switch (type) {
        case ReadingAreaEvents.ERROR:
          this.$_presentPublicationErrorHandler(payload);
          break;
        default:
          logger.warn(`Unsupported ReadingArea event type: ${type}`);
      }
    },

    $_resetProgress() {
      this.$store.dispatch('ProgressStore/clearPublicationProgress', {
        bookId: this.publicationId,
        type: this.publicationType
      });
    },

    $_clearNativeSelection() {
      if (!window.getSelection) {
        return;
      }

      if (window.getSelection().empty) {
        // Chrome
        window.getSelection().empty();
      } else if (window.getSelection().removeAllRanges) {
        // Firefox etc.
        window.getSelection().removeAllRanges();
      }
    }
  }
};
</script>

<style lang="less" src="./PresentPublication.less"></style>
